import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Progress,
  Button,
  Alert,
  Row,
  Col,
  Modal, ModalBody, ModalHeader
} from 'reactstrap';
import { Line, Doughnut } from 'react-chartjs-2';
import './AgentSidebar.css';
import {
  getAgentById,
  getAgentLogs,
  getAgentCalls,
  activateAgent,
  deactivateAgent,
  startAgent,
  stopAgent,
  restartAgent
} from '../../../store/Admin';

const AgentSidebar = ({ isOpen, onClose, agent }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);

  const {
    selectedAgent,
    agentLogs,
    agentCalls,
    loading
  } = useSelector((state) => state.Admin);

  useEffect(() => {
    if (agent && agent._id && isOpen) {
      // Load agent details
      dispatch(getAgentById({ id: agent._id }));
      // Load initial tab data
      loadTabData(activeTab);
    }
  }, [agent, isOpen]);

  useEffect(() => {
    if (agent && agent._id && isOpen) {
      loadTabData(activeTab);
    }
  }, [activeTab]);

  const loadTabData = (tab) => {
    if (!agent || !agent._id) return;

    switch (tab) {
      case 'overview':
        dispatch(getAgentById({ id: agent._id }));
        break;
      case 'calls':
        dispatch(getAgentCalls({ id: agent._id, limit: 50 }));
        break;
      case 'logs':
        dispatch(getAgentLogs({ id: agent._id, limit: 100 }));
        break;
      default:
        break;
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadTabData(activeTab);
    setTimeout(() => setRefreshing(false), 1000);
  };

  const handleAgentAction = (action) => {
    const filters = { page: 1 };
    switch (action) {
      case 'activate':
        dispatch(activateAgent({ id: agent._id, filters }));
        break;
      case 'deactivate':
        dispatch(deactivateAgent({ id: agent._id, filters }));
        break;
      case 'start':
        dispatch(startAgent({ id: agent._id, filters }));
        break;
      case 'stop':
        dispatch(stopAgent({ id: agent._id, filters }));
        break;
      case 'restart':
        dispatch(restartAgent({ id: agent._id, filters }));
        break;
    }
  };

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'secondary';
      case 'busy': return 'warning';
      case 'error': return 'danger';
      default: return 'secondary';
    }
  };

  const renderOverviewTab = () => {
    const agentData = selectedAgent?.agent || agent;
    const performance = selectedAgent?.performance || {};
    const callStatusDistribution = selectedAgent?.callStatusDistribution || [];

    return (
      <div className="tab-content-wrapper">
        {/* Agent Information Card */}
        <div className='chart-area'>
          <h4>Agent Information</h4>
          <div className='bottom-detail'>
            <ul>
              <li><strong>Name:</strong> {agentData?.name}</li>
              <li><strong>Status:</strong> <Badge color={getStatusColor(agentData?.status)}>{agentData?.status || 'active'}</Badge></li>
              <li><strong>Type:</strong> {agentData?.type || 'Single Prompt'}</li>
              <li><strong>Workspace:</strong> {agentData?.workspaceId?.name || 'N/A'}</li>
              <li><strong>Language:</strong> {agentData?.language?.label || 'English'}</li>
              <li><strong>Last Activity:</strong> {agentData?.lastActivity?.timestamp ? new Date(agentData.lastActivity.timestamp).toLocaleString() : 'N/A'}</li>
              <li><strong>Created:</strong> {new Date(agentData?.createdAt).toLocaleDateString()}</li>
            </ul>
          </div>
        </div>
        <div className="user-overview-section">
          <h4>Performance Metrics</h4>
          <div className="user-stats-grid">
            <div className="stat-card">
              <div className="stat-icon">
                <i className="fa fa-calendar text-info"></i>
              </div>
              <div className="stat-content">
                <label>Total Calls</label>
                <span className='text-info'>{performance?.totalCalls || agentData?.callStats?.totalCalls || 0}</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fa fa-clock text-success"></i>
              </div>
              <div className="stat-content">
                <label>Successful</label>
                <span className='text-success'>{performance?.totalCalls > 0
                  ? Math.round((performance.successRate || 0) * performance.totalCalls)
                  : agentData?.callStats?.successfulCalls || 0}</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fa fa-building text-warning"></i>
              </div>
              <div className="stat-content">
                <label>Today's Calls</label>
                <span className='text-warning'>{performance?.todaysCalls || 0}</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <i className="fa fa-globe text-info"></i>
              </div>
              <div className="stat-content">
                <label>Avg Duration</label>
                <span className='text-info'>{performance?.averageDuration
                  ? `${Math.floor(performance.averageDuration / 60)}m ${Math.floor(performance.averageDuration % 60)}s`
                  : agentData?.callStats?.averageCallDuration
                    ? `${Math.floor(agentData.callStats.averageCallDuration / 60)}m`
                    : '0m'}</span>
              </div>
            </div>
          </div>
          {/* Success Rate Progress */}
          <div className="chart-area mt-3">
            <div className="bottom-detail">
              <div className='progress-area'>
                <span>Success Rate</span>
                <span>
                  {performance?.successRate
                    ? `${Math.round(performance.successRate * 100)}%`
                    : performance?.totalCalls > 0
                      ? `${Math.round((performance.successfulCalls / performance.totalCalls) * 100)}%`
                      : agentData?.callStats?.totalCalls > 0
                        ? `${Math.round((agentData.callStats.successfulCalls / agentData.callStats.totalCalls) * 100)}%`
                        : '0%'}
                </span>
              </div>
            </div>
            <Progress
              value={performance?.successRate
                ? performance.successRate * 100
                : performance?.totalCalls > 0
                  ? (performance.successfulCalls / performance.totalCalls) * 100
                  : agentData?.callStats?.totalCalls > 0
                    ? (agentData.callStats.successfulCalls / agentData.callStats.totalCalls) * 100
                    : 0}
              color="success"
            />
          </div>
        </div>

        {/* Call Status Distribution */}
        {callStatusDistribution && callStatusDistribution.length > 0 && (
          <div className='chart-area'>
            <h4>Call Status Distribution</h4>
            <div className='bottom-detail'>
              <Row>
                {callStatusDistribution.map((status, index) => (
                  <Col md={4} key={index} className="text-center mb-2">
                    <div className="text-capitalize">
                      <Badge color={status._id === 'ended' ? 'success' : status._id === 'error' ? 'danger' : 'warning'}>
                        {status._id}
                      </Badge>
                      <div className="mt-1">
                        <strong>{status.count}</strong>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </div>
        )}

        <div className='chart-area'>
          <h4>Quick Actions</h4>
          <div className='bottom-detail'>
            <div className='btns-groups'>
              {agentData?.status === 'active' ? (
                <>
                  <button color="warning" size="sm" onClick={() => handleAgentAction('deactivate')}>
                    <i className="fa fa-power-off mr-1"></i> Deactivate
                  </button>
                  <button color="primary" size="sm" onClick={() => handleAgentAction('start')}>
                    <i className="fa fa-play mr-1"></i> Start
                  </button>
                  <button color="danger" size="sm" onClick={() => handleAgentAction('stop')}>
                    <i className="fa fa-stop mr-1"></i> Stop
                  </button>
                </>
              ) : (
                <button color="success" size="sm" onClick={() => handleAgentAction('activate')}>
                  <i className="fa fa-power-off mr-1"></i> Activate
                </button>
              )}
              <button color="info" size="sm" onClick={() => handleAgentAction('restart')}>
                <i className="fa fa-undo mr-1"></i> Restart
              </button>
            </div>
          </div>
        </div>
      </div >
    );
  };

  const renderConfigurationTab = () => {
    const agentData = selectedAgent?.agent || agent;

    return (
      <div className="tab-content-wrapper">
        {/* AI Model Settings */}
        <div className='chart-area'>
          <h4>AI Model Settings</h4>
          <div className='bottom-detail'>
            <ul>
              <li><strong>LLM Model:</strong> {agentData?.modelId?.modelName || agentData?.modelType || 'Not configured'}</li>
              <li><strong>Voice Name:</strong> {agentData?.voiceId?.voiceName || 'Not configured'}</li>
              <li><strong>LLM Provider:</strong> {agentData?.modelId?.provider || 'Not configured'}</li>
              <li><strong>Voice Provider:</strong> {agentData?.voiceId?.providerId?.provider || agentData?.voiceId?.voiceProvider || 'Not configured'}</li>
              <li><strong>LLM Cost:</strong> {agentData?.modelId?.cost ? `${agentData.modelId.cost} ${agentData.modelId.currency || 'USD'}` : 'N/A'}</li>
              <li><strong>Voice Gender:</strong> {agentData?.voiceId?.gender || 'Not specified'}</li>
              <li><strong>Voice Accent:</strong> {agentData?.voiceId?.accent || 'Default'}</li>
            </ul>
          </div>
        </div>
        {agentData?.sttProvider && (
          <div className='chart-area'>
            <h4>Speech-to-Text Configuration</h4>
            <div className='bottom-detail'>
              <ul>
                <li><strong>STT Provider:</strong> {agentData.sttProvider.provider || 'Default'}</li>
                <li><strong>STT Model:</strong> {agentData.sttProvider.modelName || 'Default'}</li>
                <li><strong>STT Cost:</strong> {agentData.sttProvider.cost ? `${agentData.sttProvider.cost} ${agentData.sttProvider.currency || 'USD'}` : 'N/A'}</li>
              </ul>
            </div>
          </div>
        )}

        {/* Script & Prompts */}
        <div className='chart-area'>
          <h4>Script & Prompts</h4>
          <div className='bottom-detail'>
            <div className="mb-3">
              <strong>First Message:</strong>
              <div className="mt-1 p-2 bg-light rounded">
                {agentData?.firstMessage || 'Hello, how can I help you today?'}
              </div>
            </div>
            {agentData?.script && (
              <div className="mb-3">
                <strong>Script:</strong>
                <div className="mt-1 p-2 bg-light rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <pre style={{ whiteSpace: 'pre-wrap' }}>{agentData.script}</pre>
                </div>
              </div>
            )}
            {agentData?.scriptSummary && (
              <div className="mb-3">
                <strong>Script Summary:</strong>
                <div className="mt-1 p-2 bg-light rounded">
                  {agentData.scriptSummary}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Call Settings */}
        <div className='chart-area'>
          <h4>Call Settings</h4>
          <div className='bottom-detail'>
            <ul>
              <li><strong>Max Call Duration:</strong> {agentData?.maxCallDuration ? `${agentData.maxCallDuration} seconds` : '300 seconds'}</li>
              <li><strong>Voice Mail Detection:</strong> {agentData?.voiceMailDetection ? 'Enabled' : 'Disabled'}</li>
              <li><strong>Silence Threshold:</strong> {agentData?.silenceThreshold || 30} seconds</li>
              <li><strong>Enable Recording:</strong> {agentData?.enableRecording ? 'Yes' : 'No'}</li>
              <li><strong>Pause Before Speaking:</strong> {agentData?.pauseBeforeSpeaking || 0} ms</li>
              <li><strong>Enable Transcript:</strong> {agentData?.enableTranscript ? 'Yes' : 'No'}</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderFunctionsTab = () => {
    const agentData = selectedAgent || agent;

    return (
      <div className="tab-content-wrapper">
        {/* Built-in Functions */}
        <div className='chart-area'>
          <h4>Built-in Functions</h4>
          <div className='bottom-detail'>
            <ul>
              <li><strong>End Call:</strong> {agentData?.endCall?.name ? 'Configured' : 'Not configured'} {agentData?.endCall?.description && (<p className="text-muted small mt-1">{agentData.endCall.description}</p>)}</li>
              <li><strong>Create Booking:</strong> {agentData?.createBooking?.name ? 'Configured' : 'Not configured'}  {agentData?.createBooking?.description && (<p className="text-muted small mt-1">{agentData.createBooking.description}</p>)}</li>
              <li><strong>Check Availability:</strong> {agentData?.checkAvailability?.name ? 'Configured' : 'Not configured'}  {agentData?.checkAvailability?.description && (<p className="text-muted small mt-1">{agentData.checkAvailability.description}</p>)}</li>

              <li><strong>Call Transfer:</strong> {agentData?.callTransfer?.name ? 'Configured' : 'Not configured'} {agentData?.callTransfer?.transferTo && (<p className="text-muted small mt-1">Transfer to: {agentData.callTransfer.transferTo}</p>)}</li>

            </ul>
          </div>
        </div>

        {/* Custom Functions */}
        {
          agentData?.customFunctions && agentData.customFunctions.length > 0 && (
            <div className='chart-area'>
              <h4>Custom Functions ({agentData.customFunctions.length})</h4>
              <div className='bottom-detail'>
                {agentData.customFunctions.map((func, index) => (
                  <div key={index} className="mb-2 p-2 border rounded">
                    <strong>{func.name || `Function ${index + 1}`}</strong>
                    {func.description && <p className="text-muted small mb-0">{func.description}</p>}
                  </div>
                ))}
              </div>
            </div>

          )
        }
        {/* Webhook & API */}
        <div className='chart-area'>
          <h4>Webhook & API</h4>
          <div className='bottom-detail'>
            <div className="mb-3">
              <strong>Webhook URL:</strong>
              <div className="p-2 bg-light rounded text-break">
                {agentData?.webhookURL || 'Not configured'}
              </div>
            </div>
            {agentData?.dynamicVariables && Object.keys(agentData.dynamicVariables).length > 0 && (
              <div className="mb-3">
                <strong>Dynamic Variables:</strong>
                <div className="mt-2">
                  {Object.entries(agentData.dynamicVariables).map(([key, value]) => (
                    <div key={key} className="mb-1">
                      <code>{key}</code>: {value}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div >
    );
  };

  const renderCallsTab = () => {
    const calls = agentCalls?.calls || selectedAgent?.recentCalls || [];
    const callStats = agentCalls?.statistics?.summary || {};
    const statusDistribution = agentCalls?.statistics?.statusDistribution || [];
    const sentimentDistribution = agentCalls?.statistics?.sentimentDistribution || [];

    return (
      <div className="tab-content-wrapper">
        {/* Call Statistics */}
        {Object.keys(callStats).length > 0 && (
          <div className="user-overview-section">
            <h4>Call Statistics</h4>
            <div className="user-stats-grid">
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fa fa-calendar text-info"></i>
                </div>
                <div className="stat-content">
                  <label>Total Calls</label>
                  <span className='text-info'>{callStats.totalCalls || 0}</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fa fa-clock text-success"></i>
                </div>
                <div className="stat-content">
                  <label>Successful</label>
                  <span className='text-success'>{callStats.successfulCalls || 0}</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fa fa-building text-warning"></i>
                </div>
                <div className="stat-content">
                  <label>Avg Duration</label>
                  <span className='text-warning'>{callStats.avgDuration ? `${Math.floor(callStats.avgDuration / 60)}m` : '0m'}</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fa fa-globe text-info"></i>
                </div>
                <div className="stat-content">
                  <label>Total Cost</label>
                  <span className='text-info'>${callStats.totalCost ? callStats.totalCost.toFixed(2) : '0.00'}</span>
                </div>
                <h4 className="text-warning"></h4>
                <small></small>
              </div>
            </div>
          </div>
        )}

        {/* Recent Calls Table */}
        <div className='content-dashboard'>
          <h4>Recent Calls</h4>
          <div className="table-container filters-table">
            <div className='table-area'>
              {calls && calls.length > 0 ? (
                <div className="table-responsive">
                  <Table hover size="sm">
                    <thead>
                      <tr>
                        <th>Date/Time</th>
                        <th>Duration</th>
                        <th>Status</th>
                        <th>Number</th>
                        <th>Type</th>
                        <th>Cost</th>
                        <th>Sentiment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calls.map((call) => (
                        <tr key={call._id}>
                          <td>{new Date(call.createdAt).toLocaleString()}</td>
                          <td>
                            {call.callDuration
                              ? `${Math.floor(call.callDuration / 60)}m ${call.callDuration % 60}s`
                              : `${Math.floor((call.duration || 0) / 60)}m`}
                          </td>
                          <td>
                            <Badge color={
                              call.status === 'ended' ? 'success' :
                                call.status === 'error' ? 'danger' :
                                  call.status === 'ongoing' ? 'primary' : 'warning'
                            }>
                              {call.status}
                            </Badge>
                          </td>
                          <td>{call.toNumber || call.fromNumber || 'N/A'}</td>
                          <td>
                            <Badge color="secondary" className="text-capitalize">
                              {call.callType?.replace('_', ' ') || 'voice_call'}
                            </Badge>
                          </td>
                          <td>${call.cost ? parseFloat(call.cost).toFixed(2) : '0.00'}</td>
                          <td>
                            {call.sentiment && (
                              <Badge color={
                                call.sentiment === 'positive' ? 'success' :
                                  call.sentiment === 'negative' ? 'danger' : 'warning'
                              }>
                                {call.sentiment}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <Alert color="text-warning" className="p-2 bg-light rounded">
                  No call history available for this agent.
                </Alert>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLogsTab = () => {
    const logs = agentLogs?.logs || [];
    const logStats = agentLogs?.statistics?.summary || {};
    const eventDistribution = agentLogs?.statistics?.eventDistribution || [];

    return (
      <div className="tab-content-wrapper">
        {/* Log Statistics */}
        {Object.keys(logStats).length > 0 && (
          <div className="user-overview-section">
            <h4>Log Statistics</h4>
            <div className="user-stats-grid">
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fa fa-calendar text-info"></i>
                </div>
                <div className="stat-content">
                  <label>Total Logs</label>
                  <span className='text-info'>{logStats.totalLogs || 0}</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fa fa-clock text-danger"></i>
                </div>
                <div className="stat-content">
                  <label>Errors</label>
                  <span className='text-danger'>{logStats.errorLogs || 0}</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fa fa-building text-warning"></i>
                </div>
                <div className="stat-content">
                  <label>Warnings</label>
                  <span className='text-warning'>{logStats.warningLogs || 0}</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fa fa-globe text-info"></i>
                </div>
                <div className="stat-content">
                  <label>Info</label>
                  <span className='text-info'>{logStats.infoLogs || 0}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* System Logs */}
        <div className='content-dashboard'>
          <h4>System Logs</h4>
          <div className="table-container filters-table">
            <div className='table-area'>
              {logs && logs.length > 0 ? (
                <div className="logs-container">
                  {logs.map((log, index) => (
                    <div key={log._id || index} className={`log-entry log-${log.level || 'info'} mb-2 p-2 border rounded`}>
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="d-flex align-items-center">
                          <Badge color={
                            log.level === 'error' ? 'danger' :
                              log.level === 'warning' ? 'warning' :
                                log.level === 'debug' ? 'secondary' : 'info'
                          } className="mr-2">
                            {log.level || 'info'}
                          </Badge>
                          <Badge color="light" className="text-capitalize">
                            {log.event}
                          </Badge>
                        </div>
                        <small className="text-muted">
                          {new Date(log.timestamp || log.createdAt).toLocaleTimeString()}
                        </small>
                      </div>
                      <div className="mt-2">
                        <small><strong>{log.message}</strong></small>
                        {log.callId && (
                          <div className="mt-1">
                            <small className="text-muted">Call: {log.callId}</small>
                            {log.roomName && <small className="text-muted ml-2">Room: {log.roomName}</small>}
                          </div>
                        )}
                        {log.data && Object.keys(log.data).length > 0 && (
                          <div className="mt-1">
                            <small className="text-muted">
                              {JSON.stringify(log.data, null, 2).substring(0, 100)}
                              {JSON.stringify(log.data).length > 100 && '...'}
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Alert color="text-warning" className="p-2 bg-light rounded">
                  No logs available for this agent.
                </Alert>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderKnowledgeBaseTab = () => {
    const agentData = selectedAgent || agent;

    return (
      <div className="tab-content-wrapper">
        <div className='content-dashboard'>
          <h4>Knowledge Base Files</h4>
          <div className="table-container filters-table">
            <div className='table-area'>
              {agentData?.knowledgeBase && agentData.knowledgeBase.length > 0 ? (
                <Table hover>
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentData.knowledgeBase.map((file, index) => (
                      <tr key={index}>
                        <td>
                          <i className="fa fa-file-text-o mr-2"></i>
                          {file.fileUrl ? file.fileUrl.split('/').pop() : `File ${index + 1}`}
                        </td>
                        <td>
                          <Button color="link" size="sm" href={file.fileUrl} target="_blank">
                            <i className="fa fa-download"></i> Download
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert color="text-warning" className="p-2 bg-light rounded">
                  No knowledge base files uploaded for this agent.
                </Alert>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!agent) return null;

  return (
    <>

      <Modal isOpen={isOpen} modalClassName='right right-without-bg' backdrop={false} className={`main-modal right right-side-modal style-three`}>
        <ModalHeader toggle={onClose}>
          <h3>{agent.name}
            <Badge color={getStatusColor(agent.status || 'active')} className="mr-2">
              {agent.status || 'active'}
            </Badge>
            <span className="text-muted">
              {agent.workspaceId?.name || 'Workspace'}
            </span>
          </h3>

          <div className="header-content">
            <div className="header-actions">
              <button color="link" size="sm" onClick={handleRefresh} disabled={refreshing}>
                <i className={`fa fa-undo ${refreshing ? 'fa-spin' : ''}`}></i>
              </button>
            </div>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className='filters-modal-content'>
            <div className='function-box workspace-area-detail'>
              <div className="sidebar-body">
                <div className='tabs-area'>
                  {/* Navigation Tabs */}
                  <div className='tab-header'>
                    {/* Navigation Tabs */}
                    <Nav tabs className="workspace-tabs">
                      <NavItem>
                        <NavLink
                          className={activeTab === 'overview' ? 'active' : ''}
                          onClick={() => toggle('overview')}
                        >
                          Overview
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={activeTab === 'configuration' ? 'active' : ''}
                          onClick={() => toggle('configuration')}
                        >
                          Configuration
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={activeTab === 'functions' ? 'active' : ''}
                          onClick={() => toggle('functions')}
                        >
                          Functions
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={activeTab === 'calls' ? 'active' : ''}
                          onClick={() => toggle('calls')}
                        >
                          Calls
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={activeTab === 'logs' ? 'active' : ''}
                          onClick={() => toggle('logs')}
                        >
                          Logs
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={activeTab === 'knowledge' ? 'active' : ''}
                          onClick={() => toggle('knowledge')}
                        >
                          Knowledge Base
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>
                </div>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId="overview">
                    {renderOverviewTab()}
                  </TabPane>
                  <TabPane tabId="configuration">
                    {renderConfigurationTab()}
                  </TabPane>
                  <TabPane tabId="functions">
                    {renderFunctionsTab()}
                  </TabPane>
                  <TabPane tabId="calls">
                    {renderCallsTab()}
                  </TabPane>
                  <TabPane tabId="logs">
                    {renderLogsTab()}
                  </TabPane>
                  <TabPane tabId="knowledge">
                    {renderKnowledgeBaseTab()}
                  </TabPane>
                </TabContent>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default AgentSidebar;